"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { formatMoney } from "@/lib/format";
import { useBuilderEnabled } from "@/components/layout/SiteContext";
import { Drawer } from "@/components/ui/Drawer";
import { CloseIcon } from "@/components/ui/icons";
import { CartLines } from "./CartLines";
import { useCart } from "./CartProvider";

export function CartDrawer() {
  const { cart, isOpen, close, error, pending } = useCart();
  const builderEnabled = useBuilderEnabled();
  const pathname = usePathname();
  const isEmpty = !cart || cart.lines.length === 0;

  // Following a link inside the drawer should leave it behind.
  useEffect(() => {
    close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <Drawer open={isOpen} onClose={close} label="Your cart">
      <div className="flex h-full flex-col">
        <header className="flex items-center justify-between border-b border-paper/20 px-6 py-5">
          <h2 className="font-display text-2xl">Your cart</h2>
          <button type="button" onClick={close} className="-m-2 p-2" aria-label="Close cart">
            <CloseIcon />
          </button>
        </header>

        {isEmpty ? (
          <div className="flex flex-1 flex-col items-start gap-5 px-6 py-10">
            <p className="font-serif text-xl italic">Nothing in here yet.</p>
            <p className="text-paper">Choose one of our gift boxes, or something to send on its own.</p>
            <Link href="/boxes" className="btn btn-primary" onClick={close}>
              Shop gift boxes
            </Link>
            {builderEnabled ? (
              <Link href="/build" className="link" onClick={close}>
                Build your own box
              </Link>
            ) : null}
            <Link href="/shop" className="link" onClick={close}>
              Shop all
            </Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto overscroll-contain px-6">
              <CartLines onNavigate={close} />
            </div>
            <footer className="border-t border-paper/20 bg-cocoa px-6 py-5">
              {error ? (
                <p role="alert" className="mb-3 text-[0.9375rem] text-gold">
                  {error}
                </p>
              ) : null}
              <div className="flex items-baseline justify-between text-lg">
                <span>Subtotal</span>
                <span className="tabular-nums" aria-live="polite">
                  {formatMoney(cart.cost.subtotalAmount)}
                </span>
              </div>
              <p className="mt-1 text-sm text-paper">Shipping and taxes are calculated at checkout.</p>
              <a
                href={cart.checkoutUrl}
                className="btn btn-primary mt-4 w-full aria-disabled:pointer-events-none aria-disabled:opacity-50"
                aria-disabled={pending}
              >
                Check out
              </a>
              <Link href="/cart" className="link mt-3 block text-center text-[0.9375rem]" onClick={close}>
                View cart
              </Link>
            </footer>
          </>
        )}
      </div>
    </Drawer>
  );
}
